import React from "react";
import { useTranslation } from "react-i18next";
import {
  User,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Building,
  Users,
  DollarSign,
  Baby,
  Heart,
  FileText,
  Award,
  Clock,
  CheckCircle,
  CheckCircle2,
  XCircle,
  AlertCircle,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import ContractActions from "@/components/employees/ContractActions";
import { useContractPermissions } from "@/hooks/use-contract-permissions";

const EmployeeProfile = ({ employee, onClose }) => {
  const { t } = useTranslation();
  const { canViewContract } = useContractPermissions();

  // Get initials
  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ""}${lastName?.charAt(0) || ""
      }`.toUpperCase();
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      actif: { variant: "default", icon: CheckCircle },
      suspendu: { variant: "secondary", icon: AlertCircle },
      conge: { variant: "outline", icon: Calendar },
      demissionne: { variant: "destructive", icon: XCircle },
      licencie: { variant: "destructive", icon: XCircle },
    };

    const config = statusConfig[status] || statusConfig.actif;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className="h-3 w-3" />
        {t(`employees.status.${status}`)}
      </Badge>
    );
  };

  // Get role badge
  const getRoleBadge = (role) => {
    const roleConfig = {
      admin: { variant: "default" },
      rh: { variant: "secondary" },
      manager: { variant: "outline" },
      employe: { variant: "outline" },
      stagiaire: { variant: "outline" },
    };

    const config = roleConfig[role] || roleConfig.employe;

    return (
      <Badge variant={config.variant}>{t(`employees.role.${role}`)}</Badge>
    );
  };

  // Calculate years of service
  const getYearsOfService = (hireDate) => {
    const hire = new Date(hireDate);
    const now = new Date();
    const diffTime = Math.abs(now - hire);
    const diffYears = Math.ceil(diffTime / (1000 * 60 * 60 * 24 * 365));
    return diffYears;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">
            {t("employees.profile")}
          </h2>
          <p className="text-muted-foreground">
            {t("employees.profileDescription")}
          </p>
        </div>
        <Button variant="outline" onClick={onClose}>
          {t("close")}
        </Button>
      </div>

      {/* Employee Header */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-start gap-6">
            <Avatar className="h-24 w-24">
              <AvatarImage src={employee.photo_profil} />
              <AvatarFallback className="text-2xl">
                {getInitials(employee.prenom, employee.nom)}
              </AvatarFallback>
            </Avatar>

            <div className="flex-1 space-y-4">
              <div>
                <h3 className="text-2xl font-bold">
                  {employee.prenom} {employee.nom}
                </h3>
                <p className="text-lg text-muted-foreground">
                  {employee.numero_employe}
                </p>
                {employee.immatriculation && (
                  <p className="text-sm text-muted-foreground">
                    Immatriculation: {employee.immatriculation}
                  </p>
                )}
              </div>

              <div className="flex flex-wrap gap-2">
                {getRoleBadge(employee.role)}
                {getStatusBadge(employee.statut_employe)}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">{employee.email}</span>
                </div>

                {employee.telephone && (
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{employee.telephone}</span>
                  </div>
                )}

                {employee.adresse && (
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{employee.adresse}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Contract Section */}
      {canViewContract(employee.id) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Contrat de travail
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="h-6 w-6 text-muted-foreground" />
                <span className="text-sm font-medium">
                  Contrat de {employee.prenom} {employee.nom}
                </span>
              </div>
              <ContractActions
                employee={employee}
                onContractUpdate={() => { }}
              />
            </div>
          </CardContent>
        </Card>
      )}



      {/* Employee Details */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Professional Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building className="h-5 w-5" />
              {t("employees.professionalInfo")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  {t("employees.department")}
                </label>
                <p className="text-sm">
                  {employee.departement?.nom || t("employees.noDepartment")}
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  {t("employees.generalContractType")}
                </label>
                <p className="text-sm">
                  {employee.type_contrat
                    ? t(`employees.contractType.${employee.type_contrat}`)
                    : t("employees.notProvided")}
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  {t("employees.hireDate")}
                </label>
                <p className="text-sm">
                  {new Date(employee.date_embauche).toLocaleDateString()}
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  {t("employees.yearsOfService")}
                </label>
                <p className="text-sm">
                  {getYearsOfService(employee.date_embauche)}{" "}
                  {t("employees.years")}
                </p>
              </div>

              {employee.date_fin_contrat && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    {t("employees.contractEndDate")}
                  </label>
                  <p className="text-sm">
                    {new Date(employee.date_fin_contrat).toLocaleDateString()}
                  </p>
                </div>
              )}

              {employee.salaire && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    {t("employees.salary")}
                  </label>
                  <p className="text-sm font-medium">
                    {employee.salaire.toLocaleString()} MAD
                  </p>
                </div>
              )}
            </div>

            {employee.manager && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  {t("employees.manager")}
                </label>
                <p className="text-sm">
                  {employee.manager.prenom} {employee.manager.nom}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Personal Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              {t("employees.personalInfo")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {employee.date_naissance && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    {t("employees.birthDate")}
                  </label>
                  <p className="text-sm">
                    {new Date(employee.date_naissance).toLocaleDateString()}
                  </p>
                </div>
              )}

              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  {t("employees.generalFamilySituation")}
                </label>
                <p className="text-sm">
                  {employee.situation_familiale
                    ? t(
                      `employees.familySituation.${employee.situation_familiale}`
                    )
                    : t("employees.notProvided")}
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  {t("employees.children")}
                </label>
                <p className="text-sm">
                  {employee.nombre_enfants} {t("employees.childrenCount")}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Documents administratifs */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Documents administratifs
            </div>
            <Badge
              variant={
                [
                  employee.cv_recu,
                  employee.contrat_signe_recu,
                  employee.photos_recu,
                  employee.cin_legalise_recu,
                  employee.fiche_anthropometrique_recu,
                  employee.diplomes_legalises_recu,
                  employee.certificats_travail_recu,
                  employee.cnss_carte_recu,
                  employee.rib_attestation_recu,
                  employee.cliche_pulmonaire_recu,
                ].every((doc) => doc)
                  ? "success"
                  : "destructive"
              }
              className="text-sm font-medium"
            >
              {[
                employee.cv_recu,
                employee.contrat_signe_recu,
                employee.photos_recu,
                employee.cin_legalise_recu,
                employee.fiche_anthropometrique_recu,
                employee.diplomes_legalises_recu,
                employee.certificats_travail_recu,
                employee.cnss_carte_recu,
                employee.rib_attestation_recu,
                employee.cliche_pulmonaire_recu,
              ].every((doc) => doc)
                ? "Dossier Complet"
                : "Dossier Incomplet"}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* CV */}
            <div className="flex items-center justify-between p-3 rounded-lg border">
              <div className="flex items-center gap-3">
                {employee.cv_recu ? (
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-600" />
                )}
                <span className="text-sm font-medium">CV</span>
              </div>
              {employee.cv_employee && (
                <a
                  href={employee.cv_employee}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary hover:underline"
                >
                  Voir
                </a>
              )}
            </div>

            {/* Contrat signé */}
            <div className="flex items-center justify-between p-3 rounded-lg border">
              <div className="flex items-center gap-3">
                {employee.contrat_signe_recu ? (
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-600" />
                )}
                <span className="text-sm font-medium">Contrat signé</span>
              </div>
              {employee.contrat_signe_url && (
                <a
                  href={employee.contrat_signe_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary hover:underline"
                >
                  Voir
                </a>
              )}
            </div>

            {/* Photos */}
            <div className="flex items-center justify-between p-3 rounded-lg border">
              <div className="flex items-center gap-3">
                {employee.photos_recu ? (
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-600" />
                )}
                <span className="text-sm font-medium">2 Photos</span>
              </div>
              {employee.photos_url && (
                <a
                  href={employee.photos_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary hover:underline"
                >
                  Voir
                </a>
              )}
            </div>

            {/* CIN légalisée */}
            <div className="flex items-center justify-between p-3 rounded-lg border">
              <div className="flex items-center gap-3">
                {employee.cin_legalise_recu ? (
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-600" />
                )}
                <span className="text-sm font-medium">Copie CIN légalisée</span>
              </div>
              {employee.cin_legalise_url && (
                <a
                  href={employee.cin_legalise_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary hover:underline"
                >
                  Voir
                </a>
              )}
            </div>

            {/* Fiche anthropométrique */}
            <div className="flex items-center justify-between p-3 rounded-lg border">
              <div className="flex items-center gap-3">
                {employee.fiche_anthropometrique_recu ? (
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-600" />
                )}
                <span className="text-sm font-medium">Fiche anthropométrique</span>
              </div>
              {employee.fiche_anthropometrique_url && (
                <a
                  href={employee.fiche_anthropometrique_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary hover:underline"
                >
                  Voir
                </a>
              )}
            </div>

            {/* Diplômes légalisés */}
            <div className="flex items-center justify-between p-3 rounded-lg border">
              <div className="flex items-center gap-3">
                {employee.diplomes_legalises_recu ? (
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-600" />
                )}
                <span className="text-sm font-medium">Diplômes légalisés</span>
              </div>
              {employee.diplomes_legalises_url && (
                <a
                  href={employee.diplomes_legalises_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary hover:underline"
                >
                  Voir
                </a>
              )}
            </div>

            {/* Certificats de travail */}
            <div className="flex items-center justify-between p-3 rounded-lg border">
              <div className="flex items-center gap-3">
                {employee.certificats_travail_recu ? (
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-600" />
                )}
                <span className="text-sm font-medium">Certificats de travail</span>
              </div>
              {employee.certificats_travail_url && (
                <a
                  href={employee.certificats_travail_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary hover:underline"
                >
                  Voir
                </a>
              )}
            </div>

            {/* Carte CNSS */}
            <div className="flex items-center justify-between p-3 rounded-lg border">
              <div className="flex items-center gap-3">
                {employee.cnss_carte_recu ? (
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-600" />
                )}
                <span className="text-sm font-medium">Carte CNSS / Bulletin de paie</span>
              </div>
              {employee.cnss_carte_url && (
                <a
                  href={employee.cnss_carte_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary hover:underline"
                >
                  Voir
                </a>
              )}
            </div>

            {/* Attestation RIB */}
            <div className="flex items-center justify-between p-3 rounded-lg border">
              <div className="flex items-center gap-3">
                {employee.rib_attestation_recu ? (
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-600" />
                )}
                <span className="text-sm font-medium">Attestation RIB</span>
              </div>
              {employee.rib_attestation_url && (
                <a
                  href={employee.rib_attestation_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary hover:underline"
                >
                  Voir
                </a>
              )}
            </div>

            {/* Cliché pulmonaire */}
            <div className="flex items-center justify-between p-3 rounded-lg border">
              <div className="flex items-center gap-3">
                {employee.cliche_pulmonaire_recu ? (
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-600" />
                )}
                <span className="text-sm font-medium">Cliché pulmonaire</span>
              </div>
              {employee.cliche_pulmonaire_url && (
                <a
                  href={employee.cliche_pulmonaire_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary hover:underline"
                >
                  Voir
                </a>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Additional Information */}
      <Collapsible>
        <Card>
          <CardHeader>
            <CollapsibleTrigger asChild>
              <Button
                variant="ghost"
                className="w-full justify-between p-0 h-auto"
              >
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  {t("employees.additionalInfo")}
                </CardTitle>
                <ChevronDown className="h-4 w-4" />
              </Button>
            </CollapsibleTrigger>
          </CardHeader>
          <CollapsibleContent>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Subordinates */}
                {employee.subordinates && employee.subordinates.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-3 flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      {t("employees.subordinates")} (
                      {employee.subordinates.length})
                    </h4>
                    <div className="space-y-2">
                      {employee.subordinates.map((subordinate) => (
                        <div
                          key={subordinate.id}
                          className="flex items-center gap-3 p-2 rounded-md bg-muted/50"
                        >
                          <Avatar className="h-8 w-8">
                            <AvatarFallback>
                              {getInitials(subordinate.prenom, subordinate.nom)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="text-sm font-medium">
                              {subordinate.prenom} {subordinate.nom}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {subordinate.email}
                            </p>
                          </div>
                          {getRoleBadge(subordinate.role)}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Statistics */}
                <div>
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <Award className="h-4 w-4" />
                    {t("employees.statistics")}
                  </h4>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm">
                        {t("employees.generalRole")}
                      </span>
                      {getRoleBadge(employee.role)}
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">
                        {t("employees.generalStatus")}
                      </span>
                      {getStatusBadge(employee.statut_employe)}
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">
                        {t("employees.generalContractType")}
                      </span>
                      <span className="text-sm">
                        {employee.type_contrat
                          ? t(`employees.contractType.${employee.type_contrat}`)
                          : t("employees.notProvided")}
                      </span>
                    </div>
                    {employee.salaire && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm">{t("employees.salary")}</span>
                        <span className="text-sm font-medium">
                          {employee.salaire.toLocaleString()} MAD
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* Action Buttons */}
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onClose}>
          {t("close")}
        </Button>
      </div>
    </div>
  );
};

export default EmployeeProfile;
